/**
 * "Tap a chair for Aysel", or "Tap a table to swap with Table 3" — the only
 * trace of a tap-to-seat or a table swap in progress, and the way out of one.
 * It sits at the top of the room, clear of the chairs the planner is about to
 * aim at.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { useEffect } from 'react'

import { avatarTint, initialsOf, screenSwatchFor } from '../lib/colors'
import { useT } from '../lib/i18n/I18nProvider'
import type { Plan } from '../lib/types'
import { useViewStore } from '../store/viewStore'

export function PlacingBanner({ plan }: { plan: Plan }) {
  const t = useT()
  const placing = useViewStore((s) => s.placing)
  const guest =
    placing?.kind === 'guest' ? (plan.guests.find((entry) => entry.id === placing.guestId) ?? null) : null
  const table =
    placing?.kind === 'swap' ? (plan.tables.find((entry) => entry.id === placing.tableId) ?? null) : null

  // Deleted in another tab, or undone away, while the editor waited for a tap.
  useEffect(() => {
    if (placing && !guest && !table) useViewStore.getState().stopPlacing()
  }, [placing, guest, table])

  return (
    <AnimatePresence>
      {(guest || table) && (
        <motion.div
          key="placing"
          className="placing-banner"
          role="status"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
        >
          {guest && (
            <span className="placing-avatar" style={{ background: avatarTint(guest.id) }}>
              {guest.photoUrl ? <img src={guest.photoUrl} alt="" draggable={false} /> : initialsOf(guest.fullName)}
            </span>
          )}
          {table && (
            <span className="placing-avatar" style={{ background: screenSwatchFor(table.color).edge }}>
              <SwapIcon />
            </span>
          )}
          <span className="placing-text">
            <strong>{guest ? t.placing.prompt(guest.fullName) : t.swap.prompt(table!.name)}</strong>
            <small>{guest ? t.placing.hint : t.swap.hint}</small>
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline placing-cancel"
            onClick={() => useViewStore.getState().stopPlacing()}
          >
            {t.common.cancel}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export function SwapIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M2.5 5h10m0 0L10 2.5M12.5 5 10 7.5M13.5 11h-10m0 0L6 8.5M3.5 11 6 13.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
